import { useEffect, useState } from "react";
import api from "../lib/api";
import { BellOff, UserX, Clock, ShieldAlert } from "lucide-react";

/**
 * Founder-only card summarising staff with silent push endpoints so
 * critical alerts (arrivals, cost sheets) never quietly stop landing.
 */
export default function PushHealthCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/push/health")
      .then(r => setData(r.data))
      .catch(() => setErr(true));
  }, []);

  if (err || !data) return null;
  const missing = data.missing || [];
  const stale = data.stale || [];
  if (!missing.length && !stale.length) {
    return (
      <div className="aii-card p-4 border-l-4 border-emerald-500 flex items-center gap-3" data-testid="push-health-clean">
        <ShieldAlert className="text-emerald-600 shrink-0" size={20}/>
        <div className="text-sm">Push health: every staff device is subscribed and refreshed in the last 21 days. Alerts land silently — until they need to be noisy.</div>
      </div>
    );
  }

  return (
    <div className="aii-card p-5" data-testid="push-health-card">
      <div className="flex items-center gap-2 mb-3">
        <BellOff className="text-amber-600" size={18}/>
        <h3 className="text-lg font-semibold">Push health · {missing.length + stale.length} staff to nudge</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)] mb-1.5 flex items-center gap-1"><UserX size={11}/> Never subscribed ({missing.length})</div>
          {missing.length === 0 ? (
            <div className="text-xs text-emerald-700">All good.</div>
          ) : (
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {missing.slice(0, 12).map(u => (
                <li key={u.id} className="text-xs border border-[color:var(--aii-border)] rounded px-2 py-1 flex items-center justify-between">
                  <span className="truncate font-medium">{u.name || "?"}</span>
                  <span className="text-[10px] text-[color:var(--aii-muted)] uppercase">{u.role} · {u.city || "—"}</span>
                </li>
              ))}
              {missing.length > 12 && <li className="text-[10px] text-[color:var(--aii-muted)]">+{missing.length - 12} more…</li>}
            </ul>
          )}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)] mb-1.5 flex items-center gap-1"><Clock size={11}/> Stale &gt;21 days ({stale.length})</div>
          {stale.length === 0 ? (
            <div className="text-xs text-emerald-700">All recent.</div>
          ) : (
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {stale.slice(0, 12).map(u => (
                <li key={u.id} className="text-xs border border-[color:var(--aii-border)] rounded px-2 py-1 flex items-center justify-between">
                  <span className="truncate font-medium">{u.name || "?"}</span>
                  <span className="text-[10px] text-[color:var(--aii-muted)] uppercase">{u.role} · last {u.last_seen ? new Date(u.last_seen).toLocaleDateString() : "?"}</span>
                </li>
              ))}
              {stale.length > 12 && <li className="text-[10px] text-[color:var(--aii-muted)]">+{stale.length - 12} more…</li>}
            </ul>
          )}
        </div>
      </div>
      <div className="text-[11px] text-[color:var(--aii-muted)] mt-3">Ask each staff to open the app, allow push notifications, and confirm they see a test toast — silent devices miss arrival + cost-sheet alerts.</div>
    </div>
  );
}
